import React from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SeriesEndConditionFields from './SeriesEndConditionFields';
import {
    MonthlyMode,
    NTH_LABELS_DE,
    NTH_LABELS_EN,
    RecurrencePattern,
    RecurrenceType,
    WEEKDAY_LABELS_DE,
    WEEKDAY_LABELS_EN,
    getPatternSummary,
} from '../../../../../utils/recurrence';

interface RecurrencePatternFormProps {
    pattern: RecurrencePattern;
    onChange: (pattern: RecurrencePattern) => void;
}

const RECURRENCE_TYPE_KEYS: { value: RecurrenceType, translationKey: string }[] = [
    { value: 'daily', translationKey: 'series_booking.recurrence.type_daily' },
    { value: 'weekly', translationKey: 'series_booking.recurrence.type_weekly' },
    { value: 'monthly', translationKey: 'series_booking.recurrence.type_monthly' },
    // Custom recurrence hidden until date picker is implemented (see #718)
    // { value: 'custom', translationKey: 'series_booking.recurrence.type_custom' },
];

const RecurrencePatternForm: React.FC<RecurrencePatternFormProps> = ({ pattern, onChange }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language?.startsWith('de') ? 'de' : 'en';
    const weekdayLabels = lang === 'de' ? WEEKDAY_LABELS_DE : WEEKDAY_LABELS_EN;
    const nthLabels = lang === 'de' ? NTH_LABELS_DE : NTH_LABELS_EN;

    const updatePattern = (updates: Partial<RecurrencePattern>) => {
        onChange({ ...pattern, ...updates });
    };

    const toggleWeekday = (day: number) => {
        const weekdays = pattern.weekdays.includes(day)
            ? pattern.weekdays.filter((d) => d !== day)
            : [...pattern.weekdays, day].sort((a, b) => a - b);
        updatePattern({ weekdays });
    };

    const getIntervalUnit = () => {
        if (pattern.type === 'daily') return t('series_booking.recurrence.days');
        if (pattern.type === 'weekly') return t('series_booking.recurrence.weeks');
        return t('series_booking.recurrence.months');
    };

    return (
        <div className={'rounded-lg bg-white p-6 shadow-sm'}>
            <Typography variant={'h6'} gutterBottom>
                {t('series_booking.recurrence.title')}
            </Typography>

            {/* Pattern Type Selection */}
            <div className={'mb-6'}>
                <label className={'mb-2 block text-sm font-medium text-gray-700'}>
                    {t('series_booking.recurrence.frequency')}
                </label>
                <div className={'flex gap-2'}>
                    {RECURRENCE_TYPE_KEYS.map((type) => (
                        <button
                            key={type.value}
                            onClick={() => updatePattern({ type: type.value })}
                            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                                pattern.type === type.value
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                        >
                            {t(type.translationKey)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Interval (for daily, weekly, monthly) */}
            {pattern.type !== 'custom' && (
                <div className={'mb-6'}>
                    <label className={'mb-2 block text-sm font-medium text-gray-700'}>
                        {t('series_booking.recurrence.interval')}
                    </label>
                    <div className={'flex items-center gap-2'}>
                        <span className={'text-sm text-gray-600'}>
                            {t('series_booking.recurrence.every')}
                        </span>
                        <input
                            type={'number'}
                            min={1}
                            max={52}
                            value={pattern.interval}
                            onChange={(e) => updatePattern({ interval: Math.max(1, parseInt(e.target.value) || 1) })}
                            className={
                                'w-20 rounded-md border border-gray-300 px-3 py-2 text-sm ' +
                                'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary'
                            }
                        />
                        <span className={'text-sm text-gray-600'}>
                            {getIntervalUnit()}
                        </span>
                    </div>
                </div>
            )}

            {/* Weekday Selection (for weekly) */}
            {pattern.type === 'weekly' && (
                <div className={'mb-6'}>
                    <label className={'mb-2 block text-sm font-medium text-gray-700'}>
                        {t('series_booking.recurrence.weekdays')}
                    </label>
                    <div className={'flex gap-2'}>
                        {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                            <button
                                key={day}
                                onClick={() => toggleWeekday(day)}
                                className={`flex h-10 w-10 items-center justify-center rounded-full text-sm font-medium transition-colors ${
                                    pattern.weekdays.includes(day)
                                        ? 'bg-primary text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                {weekdayLabels[day]}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Monthly Mode */}
            {pattern.type === 'monthly' && (
                <div className={'mb-6'}>
                    <label className={'mb-2 block text-sm font-medium text-gray-700'}>
                        {t('series_booking.recurrence.monthly_pattern')}
                    </label>
                    <div className={'flex flex-col gap-3'}>
                        {/* By day of month */}
                        <label className={'flex items-center gap-2'}>
                            <input
                                type={'radio'}
                                checked={pattern.monthlyMode === 'dayOfMonth'}
                                onChange={() => updatePattern({ monthlyMode: 'dayOfMonth' as MonthlyMode })}
                                className={'text-primary'}
                            />
                            <span className={'text-sm text-gray-700'}>
                                {t('series_booking.recurrence.on_the')}
                            </span>
                            <input
                                type={'number'}
                                min={1}
                                max={31}
                                value={pattern.dayOfMonth}
                                onChange={(e) => updatePattern({ dayOfMonth: Math.min(31, Math.max(1, parseInt(e.target.value) || 1)) })}
                                disabled={pattern.monthlyMode !== 'dayOfMonth'}
                                className={'w-16 rounded-md border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400'}
                            />
                            <span className={'text-sm text-gray-700'}>
                                {t('series_booking.recurrence.of_the_month')}
                            </span>
                        </label>

                        {/* By nth weekday */}
                        <label className={'flex items-center gap-2'}>
                            <input
                                type={'radio'}
                                checked={pattern.monthlyMode === 'nthWeekday'}
                                onChange={() => updatePattern({ monthlyMode: 'nthWeekday' as MonthlyMode })}
                                className={'text-primary'}
                            />
                            <span className={'text-sm text-gray-700'}>
                                {t('series_booking.recurrence.on_the')}
                            </span>
                            <select
                                value={pattern.nthWeekday.n}
                                onChange={(e) => updatePattern({ nthWeekday: { ...pattern.nthWeekday, n: parseInt(e.target.value) } })}
                                disabled={pattern.monthlyMode !== 'nthWeekday'}
                                className={'rounded-md border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400'}
                            >
                                {[1, 2, 3, 4, -1].map((n) => (
                                    <option key={n} value={n}>{nthLabels[n]}</option>
                                ))}
                            </select>
                            <select
                                value={pattern.nthWeekday.weekday}
                                onChange={(e) => updatePattern({ nthWeekday: { ...pattern.nthWeekday, weekday: parseInt(e.target.value) } })}
                                disabled={pattern.monthlyMode !== 'nthWeekday'}
                                className={'rounded-md border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50 disabled:text-gray-400'}
                            >
                                {[1, 2, 3, 4, 5, 6, 0].map((day) => (
                                    <option key={day} value={day}>{weekdayLabels[day]}</option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>
            )}

            {/* Custom Date Picker placeholder */}
            {pattern.type === 'custom' && (
                <div className={'mb-6 rounded-md border border-dashed border-gray-300 p-4'}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {t('series_booking.recurrence.custom_placeholder')}
                    </Typography>
                </div>
            )}

            {/* End Condition */}
            {pattern.type !== 'custom' && (
                <div className={'mb-6'}>
                    <SeriesEndConditionFields pattern={pattern} onChange={updatePattern} />
                </div>
            )}

            {/* Pattern Summary */}
            {pattern.type !== 'custom' && (
                <div className={'rounded-md bg-gray-50 p-3'}>
                    <Typography variant={'body2'} color={'text.secondary'}>
                        <strong>{t('series_booking.recurrence.summary')}</strong>
                        {getPatternSummary(pattern, lang)}
                        {pattern.endCondition === 'afterOccurrences'
                            ? ` — ${t('series_booking.recurrence.occurrences_count', { count: pattern.occurrences })}`
                            : pattern.endDate
                                ? ` — ${t('series_booking.recurrence.until')} ${pattern.endDate.format('DD.MM.YYYY')}`
                                : ''}
                    </Typography>
                </div>
            )}

            {/* Warning for long series */}
            {pattern.occurrences > 52 && pattern.endCondition === 'afterOccurrences' && (
                <div className={'mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800'}>
                    {t('series_booking.recurrence.long_series_warning', { count: pattern.occurrences })}
                </div>
            )}
        </div>
    );
};

export default RecurrencePatternForm;
